import { useCallback, useEffect, useMemo, useState } from "react";

import type {
  FilterConfig,
  PaginationState,
  RTKQueryHook,
} from "../interface/paginatedSelect.interface";

type PaginatedQueryResult<T, P> = ReturnType<RTKQueryHook<T, P>> & {
  paginationState: PaginationState;
  setPaginationState: React.Dispatch<React.SetStateAction<PaginationState>>;
  setOffset: (offset: number) => void;
  setLimit: (limit: number) => void;
  setQuery: (query?: string) => void;
  resetPagination: () => void;
  loadMore: () => void;
  hasMore: boolean;
};

const usePaginatedQuery = <T, P = "">(
  useQuery: RTKQueryHook<T, P>,
  baseParams?: P extends void ? never : Omit<P, "filter">,
  config: FilterConfig = {},
  skip: boolean = false,
): PaginatedQueryResult<T, P> => {
  const [paginationState, setPaginationState] = useState<PaginationState>({
    offset: 0,
    limit: 10,
  });

  const {
    offsetKey = "offset",
    limitKey = "limit",
    queryKey = "search",
    queryParamsParamKey = "filter",
    customBuilder,
  } = config;

  const filterString = useMemo(() => {
    if (customBuilder) {
      return customBuilder(paginationState);
    }

    const params = new URLSearchParams();
    params.append(offsetKey, paginationState.offset.toString());
    params.append(limitKey, paginationState.limit.toString());
    if (paginationState.query) {
      params.append(queryKey, paginationState.query);
    }

    return `?${params.toString()}`;
  }, [paginationState, offsetKey, limitKey, queryKey, customBuilder]);

  const queryParams = useMemo(
    () =>
      ({
        ...baseParams,
        [queryParamsParamKey]: filterString,
      }) as P,
    [baseParams, filterString, queryParamsParamKey],
  );

  const getQueries = useMemo((): P => {
    if (!baseParams || typeof baseParams === "string") {
      return filterString as P;
    }
    if (baseParams) {
      return queryParams as P;
    }

    return {
      [queryParamsParamKey]: filterString,
    } as P;
  }, [baseParams, queryParams, filterString, queryParamsParamKey]);

  const queryResult = useQuery(getQueries, {
    skip,
  });

  useEffect(() => {
    if (!skip || paginationState.offset === 0) {
      return;
    }

    setPaginationState((prev) => ({ ...prev, offset: 0 }));
  }, [paginationState.offset, skip]);

  const setOffset = useCallback((offset: number) => {
    setPaginationState((prev) => ({ ...prev, offset }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPaginationState((prev) => ({ ...prev, limit, offset: 0 }));
  }, []);

  const setQuery = useCallback((query?: string) => {
    setPaginationState((prev) => ({ ...prev, query, offset: 0 }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, offset: 0 }));
  }, []);

  const loadMore = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      offset: prev.offset + prev.limit,
    }));
  }, []);

  return {
    ...queryResult,
    paginationState,
    setPaginationState,
    setOffset,
    setLimit,
    setQuery,
    resetPagination,
    loadMore,
    hasMore: queryResult.data?.pagination?.totalResult
      ? paginationState.offset + (queryResult.data?.data?.length || 0) <
        queryResult.data.pagination.totalResult
      : (queryResult.data?.data?.length || 0) >= paginationState.limit,
  };
};

export default usePaginatedQuery;
